/**
 * Group VersionRecords by local calendar day, preserving the
 * incoming order (newest-first) both at the group level and within
 * each group. Used by the timeline rail to render GitHub-style
 * date headers with the cards listed underneath.
 */
import { VersionRecord } from '../state/types';

export interface VersionDateGroup {
  /** ISO calendar date in the user's local zone, e.g. "2026-05-19".
   *  Stable key for React. */
  isoDate: string;
  /** Human label, e.g. "Today", "Yesterday", or "May 19, 2026". */
  label: string;
  versions: VersionRecord[];
}

const LONG_DATE = new Intl.DateTimeFormat(undefined, {
  year: 'numeric',
  month: 'long',
  day: 'numeric',
});

function isoLocalDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function labelFor(date: Date, now: Date): string {
  const dayMs = 24 * 60 * 60 * 1000;
  const diffDays = Math.round(
    (startOfDay(now).getTime() - startOfDay(date).getTime()) / dayMs,
  );
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  return LONG_DATE.format(date);
}

export function groupVersionsByDate(
  versions: VersionRecord[],
  now: Date = new Date(),
): VersionDateGroup[] {
  const groups: VersionDateGroup[] = [];
  let current: VersionDateGroup | null = null;
  for (const v of versions) {
    const created = new Date(v.created_at);
    const iso = isoLocalDate(created);
    if (!current || current.isoDate !== iso) {
      current = {
        isoDate: iso,
        label: labelFor(created, now),
        versions: [],
      };
      groups.push(current);
    }
    current.versions.push(v);
  }
  return groups;
}
